import React from 'react';
import dayjs, {Dayjs} from 'dayjs';
import _ from 'lodash';
import {Button, Card, DatePicker, Form, Input, Select, Spin} from 'antd';
import {
    useGetDriverLicenceQuery,
    useUpdateDriverLicenceMutation
} from '../../store/api/driverLicenceApi';
import {useAddDriverLicenceAndLinkProfile} from '../../hooks/useAddDriverLicenceAndLinkProfile';
import {selectCurrentProfile, selectIsStaff} from '../../store/slices/userSlice';
import {useGetCountries} from '../../hooks/useGetCountries';
import {useAppSelector} from '../../hooks/useStore';

import {DriverLicence} from '../../types/driverLicenceTypes';
import {BaseError} from '../../types/apiTypes';
import {messageType, showMessages} from '../../helpers/message';
import {formatDate} from '../../helpers/misc';
import {dateFormat} from '../../lib/time';
import {nothingToUpdate} from '../../helpers/text';

import {formInCardLayout, formItemFullWidth} from '../../assets/styles';

const DriverLicenceCard = () => {
    const [form] = Form.useForm();
    const profileID = useAppSelector(selectCurrentProfile)?.id;
    const isStaff = useAppSelector(selectIsStaff);

    const {data: driverLicenceData, isFetching: loadingDriverLicence} = useGetDriverLicenceQuery(isStaff ? profileID : undefined, {refetchOnMountOrArgChange: true});
    const {addDriverLicence, isLoading: addLoading} = useAddDriverLicenceAndLinkProfile({profileID});
    const [updateDriverLicence, {
        isSuccess: updateSuccess,
        isLoading: updateLoading,
        isError: updateError,
        error: updateErrorData,
        reset: updateReset
    }] = useUpdateDriverLicenceMutation();

    const {countries, provinces, setCurrentIndex, countriesLoading} = useGetCountries(
        driverLicenceData?.country
    );

    const dlLoading =  loadingDriverLicence || updateLoading || addLoading || countriesLoading;

    React.useEffect(() => {
        // Populate form fields if driver licence data exists.
        if (driverLicenceData) {
            form.setFieldsValue({
                'record_number': driverLicenceData.record_number,
                'jurisdiction': driverLicenceData.jurisdiction,
                'expiry_date': driverLicenceData.expiry_date ? dayjs(driverLicenceData.expiry_date) : null,
                'country': driverLicenceData.country,
            });
        }
    }, [driverLicenceData, form]);

    React.useEffect(() => {
        if (updateSuccess) {
            showMessages("Driver's licence successfully updated.", messageType.success);
            updateReset();
        } else if (updateError) {
            const errorMessage = (updateErrorData as BaseError)?.data?.message;
            showMessages(errorMessage ?? "Error saving driver's licence.", messageType.error);
        }
    }, [updateError, updateErrorData, updateReset, updateSuccess]);

    const disabledDate = (current: Dayjs | null) => {
        return current ? current.isBefore(dayjs().startOf('day')) : false;
    };

    const validateRecordNumber = (_: any, value: string) => {
        if (value === undefined || !/^[a-zA-Z0-9 ]+$/.test(value)) {
            return Promise.reject();
        }
        return Promise.resolve();
    };

    const validateExpiryDate = (_: any, value: string) => {
        if (!value) {
            return Promise.reject();
        }
        return Promise.resolve();
    };

    const onFinish = (values: Partial<DriverLicence>) => {
        // Prep default values.
        values['type'] = 'driver_licence';
        if (!values['jurisdiction']) values['jurisdiction'] = null;

        if (!driverLicenceData) {
            if (!isStaff) {
                showMessages("There is no driver's licence on file. Please contact staff.", messageType.error);
                return;
            }

            // If driverLicenceData is empty, add a new record.
            addDriverLicence(values as DriverLicence);
            return;
        }

        // If driversLicenceData is not empty, update the existing record.
        values['id'] = driverLicenceData.id;

        // Format date for comparison against driverLicenceData.
        values['expiry_date'] = formatDate(values['expiry_date']);

        if (!isStaff) {
            // Filter out empty values from member facing form.
            values = _.pickBy(values);
        }

        // Check if values have been updated.
        if (_.isEqual(values, driverLicenceData)) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        updateDriverLicence(values);
    };

    return (
        <Card title="Driver's Licence" className="driver-licence-card-main" bordered={true}>
            <Spin spinning={dlLoading}>
                <Form
                    form={form}
                    name="dl-staff"
                    layout="vertical"
                    {...formInCardLayout}
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="record_number"
                        label="Record Number"
                        rules={[{
                            type: 'string',
                            required: isStaff,
                            message: 'Please enter a valid Record Number.',
                            validator: isStaff ? validateRecordNumber : undefined
                        }]}
                    >
                        <Input disabled={!isStaff}/>
                    </Form.Item>
                    <Form.Item
                        name="country"
                        label="Country"
                        rules={[{
                            required: isStaff,
                            message: 'Country is required.',
                        }]}
                    >
                        <Select
                            disabled={!isStaff}
                            className="price-plan-select"
                            loading={loadingDriverLicence}
                            dropdownMatchSelectWidth={false}
                            value={driverLicenceData?.country}
                            options={countries}
                            onChange={(value) => {
                                const countryIndex = countries?.findIndex((c) => c.value === value) ?? null;
                                setCurrentIndex({
                                    country: countryIndex,
                                    province: null,
                                });
                                // Auto fill BC for Canada.
                                let newProvince = null;
                                if (value === 'Canada') newProvince = 'British Columbia';
                                form.setFieldValue('jurisdiction', newProvince);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="jurisdiction"
                        label="Jurisdiction"
                        rules={[{
                            // Only required if staff and provinces exist for current country.
                            required: isStaff && provinces ? !!provinces.length : isStaff,
                            message: 'Jurisdiction is required.'
                        }]}
                    >
                        <Select
                            placeholder={isStaff && !form.getFieldValue('country') ? 'Please select a Country' : ''}
                            disabled={!isStaff}
                            className="price-plan-select"
                            loading={loadingDriverLicence}
                            dropdownMatchSelectWidth={false}
                            value={driverLicenceData?.jurisdiction}
                            options={provinces}
                        />
                    </Form.Item>
                    <Form.Item
                        name="expiry_date"
                        label="Expiry Date"
                        rules={[{
                            type: 'object',
                            required: true,
                            message: 'Please select a valid Expiry Date.',
                            validator: validateExpiryDate
                        }]}
                    >
                        <DatePicker disabledDate={disabledDate} style={{...formItemFullWidth}} format={dateFormat}/>
                    </Form.Item>
                    <Button type="primary" htmlType="submit">
                        Save
                    </Button>
                </Form>
            </Spin>
        </Card>
    );
};

export default DriverLicenceCard;
